import Phaser from 'phaser';
import { audio } from '../game/AudioManager.js';
import { questManager } from '../game/QuestManager.js';
import { TouchControls } from '../game/TouchControls.js';

export class CastleEscapeScene extends Phaser.Scene {
  constructor() {
    super('CastleEscapeScene');
    this.player = null;
    this.queen = null;
    this.floor = null;
    this.isLeaping = false;
    this.debrisTimer = null;
    this.touchControls = null;
    this.touchMoveX = 0;
  }

  create() {
    this.isLeaping = false;
    questManager.show(true);
    questManager.setChapter("ESCAPE", "2026");
    questManager.setQuest("ESCAPE TOGETHER!", "[A/D] Sprint With Labonno | [W/SPACE] Leap Hand-in-Hand Over Rubble");
    audio.playHodBattleMusic();

    const { width, height } = this.scale;

    // 1. Maintain True Aspect Ratio for Collapsing Castle Wallpaper (1672 x 941)
    const bgScale = height / 941;
    const panelW = 1672 * bgScale;
    const worldW = panelW * 2;

    // Panel 1: Main fortress arches & flames
    this.add.image(0, 0, 'bg_collapsing_castle')
      .setOrigin(0, 0)
      .setScale(bgScale)
      .setDepth(0);

    // Panel 2: Continuous castle corridor leading to daylight
    this.add.image(panelW - 2, 0, 'bg_collapsing_castle')
      .setOrigin(0, 0)
      .setScale(bgScale)
      .setDepth(0);

    // 2. Atmospheric Fiery Vignette & Volumetric Smoke
    const fieryGlow = this.add.graphics();
    fieryGlow.fillGradientStyle(0x991b1b, 0x450a0a, 0x1f1305, 0x000000, 0.32);
    fieryGlow.fillRect(0, 0, worldW, height);
    fieryGlow.setDepth(1);

    // Pulsing warm flame ambient light across the castle corridor
    this.tweens.add({
      targets: fieryGlow,
      alpha: 0.65,
      duration: 1400,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });

    // 3. Radiant Sunlight Portal at the Far End (The Gateway to Forever)
    const portalX = worldW - 140;
    const portalGlow = this.add.graphics().setDepth(3);
    portalGlow.fillGradientStyle(0xffedd5, 0xfde047, 0xfb7185, 0x000000, 0.85);
    portalGlow.fillCircle(portalX, height * 0.52, 220);

    const sunray = this.add.graphics().setDepth(3);
    sunray.fillStyle(0xffffff, 0.16);
    sunray.fillTriangle(portalX, 0, portalX - 350, height, portalX + 80, height);

    // 4. Floating Fire Embers & Rising Ash Particle Emitter
    this.add.particles(0, 0, 'petal_particle', {
      x: { min: 0, max: worldW },
      y: height - 10,
      lifespan: 3600,
      speedY: { min: -50, max: -150 },
      speedX: { min: -25, max: 40 },
      scale: { start: 0.9, end: 0.2 },
      alpha: { start: 0.9, end: 0.05 },
      tint: [0xff4500, 0xff8c00, 0xffd700, 0xff2a6d],
      quantity: 3,
      frequency: 110
    }).setDepth(15);

    this.cameras.main.setBounds(0, 0, worldW, height);
    this.physics.world.setBounds(0, 0, worldW + 200, height + 100);

    // 5. Solid Stone Bridge Floor (platformer floor aligned with visible stone walkway)
    const floorY = height * 0.638;
    const floorH = height - floorY + 40;
    this.floor = this.add.rectangle(worldW / 2, floorY + floorH / 2, worldW + 400, floorH, 0x000000, 0)
      .setDepth(2);
    this.physics.add.existing(this.floor, true);
    this.floor.body.setSize(worldW + 400, floorH).setOffset(0, 0);

    // Subtle dark castle stone trim along bridge surface
    const floorTrim = this.add.graphics().setDepth(2);
    floorTrim.fillStyle(0x18101a, 0.5);
    floorTrim.fillRect(0, floorY, worldW - 160, 4);

    // 6. Knight Talha (Main Player with Physics)
    this.player = this.physics.add.sprite(220, floorY - 50, 'knight_talha_sheet')
      .setScale(0.40)
      .setDepth(10)
      .play('knight_run');
    this.player.setCollideWorldBounds(true);
    this.player.body.setGravityY(1100);
    this.player.body.setSize(56, 150).setOffset(100, 91);
    this.physics.add.collider(this.player, this.floor);

    // 7. Queen Labonno (Hand-in-Hand Companion)
    // Directly positioned and animated relative to Talha so she never snags, lags, or gets stuck
    this.queen = this.add.sprite(this.player.x - 44, this.player.y, 'queen_emily_sheet')
      .setScale(0.40)
      .setDepth(10)
      .play('queen_run');

    this.cameras.main.startFollow(this.player, true, 0.08, 0.08);

    // 8. Falling castle debris rocks to dodge
    this.debrisGroup = this.physics.add.group();
    this.debrisTimer = this.time.addEvent({
      delay: 360,
      loop: true,
      callback: () => {
        if (this.isLeaping) return;
        audio.playCastleRumble();
        this.cameras.main.shake(120, 0.005);
        const rx = Phaser.Math.Between(this.player.x - 250, this.player.x + 350);
        const rock = this.debrisGroup.create(rx, -20, 'debris_rock').setDepth(12);
        rock.setVelocityY(Phaser.Math.Between(260, 480));
        rock.setAngularVelocity(Phaser.Math.Between(-180, 180));
      }
    });

    this.cursors = this.input.keyboard.createCursorKeys();
    this.wasd = this.input.keyboard.addKeys({
      up: Phaser.Input.Keyboard.KeyCodes.W,
      down: Phaser.Input.Keyboard.KeyCodes.S,
      left: Phaser.Input.Keyboard.KeyCodes.A,
      right: Phaser.Input.Keyboard.KeyCodes.D,
      jump: Phaser.Input.Keyboard.KeyCodes.SPACE
    });

    // In-game dynamic dialogue toast
    this.statusToast = this.add.text(this.player.x, floorY - 85, 'HOLDING HANDS • SPRINT TOGETHER!', {
      fontFamily: 'Trebuchet MS, Arial, sans-serif',
      fontSize: '16px',
      fontWeight: 'bold',
      color: '#ffd166',
      backgroundColor: 'rgba(0,0,0,0.85)',
      padding: { x: 12, y: 5 },
      stroke: '#000',
      strokeThickness: 3
    }).setOrigin(0.5).setDepth(25);

    this.tweens.add({
      targets: this.statusToast,
      alpha: 0,
      y: floorY - 110,
      duration: 3200,
      ease: 'Power1',
      onComplete: () => this.statusToast.destroy()
    });

    // Mount Universal Touch Controls (Adventure mode)
    this.touchControls = new TouchControls(this, 'adventure', {
      onMove: (x, y) => {
        this.touchMoveX = x;
        this.touchMoveY = y;
      },
      onJump: () => {
        if (!this.player || !this.player.body || this.isLeaping) return;
        const onFloor = this.player.body.blocked.down || this.player.body.touching.down;
        if (onFloor) {
          this.player.setVelocityY(-560);
          audio.playJump();
        }
      },
      onInteract: () => {
        if (!this.player || !this.player.body || this.isLeaping) return;
        const onFloor = this.player.body.blocked.down || this.player.body.touching.down;
        if (onFloor) {
          this.player.setVelocityY(-560);
          audio.playJump();
        }
      }
    });
    this.touchControls.mount();
    this.touchControls.setInteractHighlight(true, 'LEAP');

    this.events.on('shutdown', () => {
      if (this.touchControls) {
        this.touchControls.destroy();
        this.touchControls = null;
      }
    });
  }

  update() {
    if (!this.player || !this.player.body || this.isLeaping) return;

    const runSpeed = 300;
    const onFloor = this.player.body.blocked.down || this.player.body.touching.down;

    // Platformer Left / Right movement along stone bridge floor
    let moving = false;
    const keyLeft = this.cursors.left.isDown || this.wasd.left.isDown;
    const keyRight = this.cursors.right.isDown || this.wasd.right.isDown;
    const touchX = this.touchMoveX || 0;
    const wantsLeft = keyLeft || touchX < -0.15;
    const wantsRight = keyRight || touchX > 0.15;

    if (wantsLeft) {
      const speedScale = keyLeft ? 1 : Math.min(1, Math.max(0.45, Math.abs(touchX)));
      this.player.setVelocityX(-runSpeed * speedScale);
      this.player.setFlipX(true);
      moving = true;
    } else if (wantsRight) {
      const speedScale = keyRight ? 1 : Math.min(1, Math.max(0.45, Math.abs(touchX)));
      this.player.setVelocityX(runSpeed * speedScale);
      this.player.setFlipX(false);
      moving = true;
    } else {
      this.player.setVelocityX(0);
    }

    // Keep Queen running hand-in-hand directly beside Talha without physics collision conflicts
    if (this.queen) {
      const followOffset = this.player.flipX ? 44 : -44;
      this.queen.x = this.player.x + followOffset;
      this.queen.y = this.player.y;
      this.queen.setFlipX(this.player.flipX);

      // Synchronize Queen animations with Talha
      if (!onFloor) {
        if (this.queen.anims.currentAnim?.key !== 'queen_leap') this.queen.play('queen_leap', true);
      } else if (moving) {
        if (this.queen.anims.currentAnim?.key !== 'queen_run') this.queen.play('queen_run', true);
      } else {
        if (this.queen.anims.currentAnim?.key !== 'queen_idle') this.queen.play('queen_idle', true);
      }
    }

    // Platformer Jump together (W or SPACE or Up Arrow)
    const wantsJump = Phaser.Input.Keyboard.JustDown(this.wasd.up) ||
      Phaser.Input.Keyboard.JustDown(this.wasd.jump) ||
      Phaser.Input.Keyboard.JustDown(this.cursors.up);

    if (wantsJump && onFloor) {
      this.player.setVelocityY(-560);
      audio.playJump();
    }

    // Player animation states
    if (!onFloor) {
      if (this.player.anims.currentAnim?.key !== 'knight_jump') this.player.play('knight_jump', true);
    } else if (moving) {
      if (this.player.anims.currentAnim?.key !== 'knight_run') this.player.play('knight_run', true);
    } else {
      if (this.player.anims.currentAnim?.key !== 'knight_idle') this.player.play('knight_idle', true);
    }

    // Clean up debris rocks that fell through
    this.debrisGroup.children.each((rock) => {
      if (rock && rock.y > this.scale.height + 40) {
        rock.destroy();
      }
    });

    // Reached the broken bridge chasm edge -> make the final grand safety leap!
    const worldW = (this.scale.height / 941) * 1672 * 2;
    if (this.player.x > worldW - 320) {
      this.triggerFinalSafetyLeap();
    }
  }

  triggerFinalSafetyLeap() {
    if (this.isLeaping) return;
    this.isLeaping = true;
    if (this.debrisTimer) this.debrisTimer.remove();
    if (this.touchControls) this.touchControls.show(false);

    // Disable physics body so there is no collision friction or boundary snapping during the leap
    if (this.player && this.player.body) {
      this.player.body.enable = false;
    }

    this.player.play('knight_leap');
    if (this.queen) {
      this.queen.play('queen_leap');
    }

    audio.playRomanticChime();
    audio.playCastleRumble();

    // Flash of radiant heavenly light and cinematic slow motion
    this.cameras.main.flash(800, 255, 245, 230);
    this.cameras.main.shake(600, 0.012);

    const leapDistanceX = 420;
    const leapTargetX = this.player.x + leapDistanceX;
    const leapTargetY = this.player.y - 130;

    // Smooth cinematic leap across the abyss
    this.tweens.add({
      targets: this.player,
      x: leapTargetX,
      y: leapTargetY,
      duration: 1500,
      ease: 'Quad.easeOut'
    });

    if (this.queen) {
      this.tweens.add({
        targets: this.queen,
        x: leapTargetX - 44,
        y: leapTargetY,
        duration: 1500,
        ease: 'Quad.easeOut'
      });
    }

    // Fade to white and transition to the Garden of Forever
    this.time.delayedCall(1200, () => {
      audio.stopHodBattleMusic(800);
      this.cameras.main.fade(700, 255, 255, 255);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        this.scene.start('GardenEndingScene');
      });
    });
  }
}
